<nz-layout class="main-layout">
  <nz-layout class="content-body" #contentBody (scroll)="onContentScroll($event)">
    <nz-header class="top-header" [class.is-scrolled]="scrolled()">
      <div class="header-left">
        <!-- Compact screens only: opens the navigation drawer -->
        <button
          class="header-toggle"
          nz-button
          nzType="text"
          nzShape="circle"
          (click)="toggleMenu()"
          [attr.aria-expanded]="isMenuOpen()"
          [attr.aria-label]="'Toggle navigation' | t"
        >
          <span nz-icon nzType="menu"></span>
        </button>

        <a routerLink="/home" class="header-brand">
          <img
            class="header-brand__logo"
            src="../../../assets/logos/xfabricsVerticalWhite.png"
            alt=""
          />
          <span class="header-brand__copy">
            <strong class="header-brand__name">{{ 'XFabrics B2B' | t }}</strong>
            <span class="header-brand__tagline">{{
              'Premium fabrics & custom quotations' | t
            }}</span>
          </span>
        </a>
      </div>

      <!-- Primary navigation — horizontal on desktop, drawer below 1024px -->
      <nav class="header-nav" [attr.aria-label]="'Main navigation' | t">
        @for (item of navigationItems; track item.label) {
          @if (item.children?.length) {
            <button
              type="button"
              class="header-nav__link header-nav__link--parent"
              [class.is-active]="isGroupActive(item)"
              [class.is-open]="openGroup() === item.label"
              nz-dropdown
              [nzDropdownMenu]="groupMenu"
              nzTrigger="click"
              nzPlacement="bottomCenter"
              nzOverlayClassName="header-menu"
              [attr.aria-expanded]="openGroup() === item.label"
              (nzVisibleChange)="onGroupVisibleChange(item, $event)"
            >
              {{ item.label | t }}
              <span nz-icon nzType="down" class="header-nav__caret"></span>
            </button>
            <nz-dropdown-menu #groupMenu="nzDropdownMenu">
              <ul nz-menu>
                @for (child of item.children ?? []; track child.route) {
                  <li
                    nz-menu-item
                    [routerLink]="child.route"
                    routerLinkActive
                    #childLink="routerLinkActive"
                    [nzSelected]="childLink.isActive"
                  >
                    <span nz-icon [nzType]="child.icon" style="margin-right: 8px"></span>
                    {{ child.label | t }}
                  </li>
                }
              </ul>
            </nz-dropdown-menu>
          } @else {
            <a class="header-nav__link" [routerLink]="item.route" routerLinkActive="is-active">
              {{ item.label | t }}
            </a>
          }
        }
      </nav>

      <div class="header-actions">
        <!-- Always visible: Cart, Quotation Cart. Favourites lives in the account menu. -->
        <div class="header-utils">
          <!-- Opens the search band under the bar rather than widening the header: the
               suggestion panel needs room the navbar does not have at this width. -->
          <button
            nz-button
            nzType="default"
            nzShape="circle"
            type="button"
            class="icon-link"
            aria-controls="header-search"
            [class.is-active]="searchOpen()"
            [attr.aria-expanded]="searchOpen()"
            [attr.aria-label]="'Search fabrics' | t"
            (click)="toggleSearch()"
          >
            <span nz-icon [nzType]="searchOpen() ? 'close' : 'search'"></span>
          </button>

          <nz-badge
            [nzCount]="cartCount"
            [nzShowZero]="false"
            class="header-badge"
            [class.badge-bump]="cartBump()"
          >
            <a
              nz-button
              nzType="default"
              nzShape="circle"
              routerLink="/cart"
              class="icon-link"
              [attr.aria-label]="'Open cart' | t"
            >
              <span nz-icon nzType="shopping-cart"></span>
            </a>
          </nz-badge>

          <nz-badge
            [nzCount]="quotationCartCount"
            [nzShowZero]="false"
            class="header-badge"
            [class.badge-bump]="quotationBump()"
          >
            <a
              nz-button
              nzType="default"
              nzShape="circle"
              routerLink="/quotations/new"
              class="icon-link"
              [attr.aria-label]="'Open quotation request' | t"
            >
              <span nz-icon nzType="file-text"></span>
            </a>
          </nz-badge>
        </div>

        <span class="header-divider hide-on-small" aria-hidden="true"></span>

        <div class="header-account">
          <!-- Desktop only: the account menu, which carries profile, language and logout -->
          <button
            nz-button
            nzType="text"
            type="button"
            class="profile-chip hide-on-small"
            nz-dropdown
            [nzDropdownMenu]="accountMenu"
            nzTrigger="click"
            nzPlacement="bottomRight"
            nzOverlayClassName="header-menu"
            [attr.aria-expanded]="accountMenuOpen()"
            [attr.aria-label]="'Profile' | t"
            (nzVisibleChange)="accountMenuOpen.set($event)"
          >
            <span class="user-avatar">{{ userInitials() }}</span>
            <span class="user-name">{{ userDisplayName() }}</span>
            <span nz-icon nzType="down" class="profile-caret" [class.is-open]="accountMenuOpen()">
            </span>
          </button>
          <nz-dropdown-menu #accountMenu="nzDropdownMenu">
            <ul nz-menu>
              <li
                nz-menu-item
                routerLink="/profile"
                routerLinkActive
                #profileLink="routerLinkActive"
                [nzSelected]="profileLink.isActive"
              >
                <span nz-icon nzType="user" style="margin-right: 8px"></span>
                {{ 'Profile' | t }}
              </li>
              <li
                nz-menu-item
                routerLink="/brands"
                routerLinkActive
                #brandsLink="routerLinkActive"
                [nzSelected]="brandsLink.isActive"
              >
                <span nz-icon nzType="tag" style="margin-right: 8px"></span>
                {{ 'My brands' | t }}
              </li>
              <li
                nz-menu-item
                routerLink="/favorites"
                routerLinkActive
                #favoritesLink="routerLinkActive"
                [nzSelected]="favoritesLink.isActive"
              >
                <span nz-icon nzType="heart" style="margin-right: 8px"></span>
                {{ 'Favorites' | t }}
              </li>
              <li nz-menu-divider></li>
              <li nz-menu-item (click)="setLanguage('en')">
                <span
                  nz-icon
                  nzType="check"
                  class="language-check"
                  [class.is-visible]="i18n.language() === 'en'"
                ></span>
                English
              </li>
              <li nz-menu-item (click)="setLanguage('pt')">
                <span
                  nz-icon
                  nzType="check"
                  class="language-check"
                  [class.is-visible]="i18n.language() === 'pt'"
                ></span>
                Português
              </li>
              <li nz-menu-divider></li>
              <li nz-menu-item (click)="logout()">
                <span nz-icon nzType="sign-out" style="margin-right: 8px"></span>
                {{ 'Logout' | t }}
              </li>
            </ul>
          </nz-dropdown-menu>

          <!-- Small screens only: "More" dropdown -->
          <button
            nz-button
            nzType="default"
            nzShape="circle"
            type="button"
            nz-dropdown
            [nzDropdownMenu]="moreMenu"
            nzTrigger="click"
            nzPlacement="bottomRight"
            [attr.aria-label]="'More options' | t"
            class="icon-link show-on-small"
          >
            <span nz-icon nzType="ellipsis"></span>
          </button>
        </div>
        <nz-dropdown-menu #moreMenu="nzDropdownMenu">
          <ul nz-menu>
            <li nz-menu-item routerLink="/profile">
              <span nz-icon nzType="user" style="margin-right: 8px"></span>
              {{ userDisplayName() }}
            </li>
            <li nz-menu-item routerLink="/brands">
              <span nz-icon nzType="tag" style="margin-right: 8px"></span>
              {{ 'My brands' | t }}
            </li>
            <li nz-menu-item routerLink="/favorites">
              <span nz-icon nzType="heart" style="margin-right: 8px"></span>
              {{ 'Favorites' | t }}
            </li>
            <li nz-menu-divider></li>
            <li nz-menu-item (click)="setLanguage('en')">
              <span
                nz-icon
                nzType="check"
                class="language-check"
                [class.is-visible]="i18n.language() === 'en'"
              ></span>
              English
            </li>
            <li nz-menu-item (click)="setLanguage('pt')">
              <span
                nz-icon
                nzType="check"
                class="language-check"
                [class.is-visible]="i18n.language() === 'pt'"
              ></span>
              Português
            </li>
            <li nz-menu-divider></li>
            <li nz-menu-item (click)="logout()">
              <span nz-icon nzType="sign-out" style="margin-right: 8px"></span>
              {{ 'Logout' | t }}
            </li>
          </ul>
        </nz-dropdown-menu>
      </div>
    </nz-header>

    <!-- Sticks under the bar, so the panel stays with its field while the page scrolls. -->
    @if (searchOpen()) {
      <div class="header-search" id="header-search" (keydown.escape)="closeSearch()">
        <app-fabric-search class="header-search__field"></app-fabric-search>
      </div>
    }

    @if (isNavigating()) {
      <div class="route-loading-bar" aria-live="polite" aria-label="Loading page"></div>
    }

    <nz-content class="content-shell">
      <div class="content-surface">
        <router-outlet></router-outlet>
      </div>
    </nz-content>

    <app-site-footer></app-site-footer>
    <app-fabric-comparison-banner></app-fabric-comparison-banner>
  </nz-layout>

  <!-- Off-canvas navigation drawer — compact screens (≤1024px) -->
  <aside
    class="nav-drawer"
    [class.is-open]="isMenuOpen()"
    [attr.inert]="isMenuOpen() ? null : ''"
    [attr.aria-label]="'Main navigation' | t"
  >
    <div class="nav-drawer__head">
      <div class="nav-drawer__brand">
        <img src="../../../assets/logos/xfabricsVerticalWhite.png" alt="" />
        <div class="nav-drawer__copy">
          <strong>XFabrics</strong>
          <span>{{ 'Quotation Workspace' | t }}</span>
        </div>
      </div>
      <button
        nz-button
        nzType="text"
        nzShape="circle"
        type="button"
        class="nav-drawer__close"
        (click)="closeMenu()"
        [attr.aria-label]="'Close' | t"
      >
        <span nz-icon nzType="close"></span>
      </button>
    </div>

    <nav class="nav-drawer__nav" [attr.aria-label]="'Main navigation' | t">
      <ul class="nav-drawer__list">
        @for (item of navigationItems; track item.label) {
          <li>
            @if (item.children?.length) {
              <!-- Grouped items expand inline: a popup inside an off-canvas drawer reads badly on touch. -->
              <span class="nav-drawer__group">
                <span nz-icon [nzType]="item.icon"></span>
                <span>{{ item.label | t }}</span>
              </span>
              <ul class="nav-drawer__sublist">
                @for (child of item.children ?? []; track child.route) {
                  <li>
                    <a
                      class="nav-drawer__link"
                      [routerLink]="child.route"
                      routerLinkActive="is-active"
                      (click)="closeMenu()"
                    >
                      <span nz-icon [nzType]="child.icon"></span>
                      <span>{{ child.label | t }}</span>
                    </a>
                  </li>
                }
              </ul>
            } @else {
              <a
                class="nav-drawer__link"
                [routerLink]="item.route"
                routerLinkActive="is-active"
                (click)="closeMenu()"
              >
                <span nz-icon [nzType]="item.icon"></span>
                <span>{{ item.label | t }}</span>
              </a>
            }
          </li>
        }
      </ul>
    </nav>
  </aside>

  <div
    class="menu-backdrop"
    [class.is-open]="isMenuOpen()"
    (click)="closeMenu()"
    aria-hidden="true"
  ></div>
</nz-layout>
